/**
 * A dialog that asks for one line of text and posts it (plan
 * docs/plans/lms-plan.md 12.3 "Not applicable with a reason", Appendix A
 * "cancel {reason}", 12.3 "notes"; WP-47): the hire's and the admin's "Not
 * applicable", the admin's run cancel and the admin's note. The text is
 * checked for length here and for credentials by the server (G14), whose
 * refusal is shown in plain words.
 */
import { useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from '../../../ui/Modal';
import { dangerBtnCls, fieldCls, primaryBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { ONBOARDING_LIMITS } from '../../api/onboarding';
import { describeOnboardingError } from './shared';

export interface ReasonDialogProps {
  isOpen: boolean;
  title: string;
  /** One line above the field (for example the item's title). */
  intro?: ReactNode;
  label: string;
  hint?: string;
  confirmLabel: string;
  /** The dismiss button's word (default "Cancel"). */
  cancelLabel?: string;
  /** The destructive button (a run cancel). */
  danger?: boolean;
  /** Allow an empty text (a note is cleared by saving it empty). */
  optional?: boolean;
  initialValue?: string;
  onClose: () => void;
  /** Posts the text; a rejection is shown in the dialog, a success is the caller's to close. */
  onSubmit: (text: string) => Promise<void>;
  /** The data-testid prefix: <testId>-form, -text, -save, -error. */
  testId: string;
}

export function ReasonDialog({
  isOpen,
  title,
  intro,
  label,
  hint,
  confirmLabel,
  cancelLabel = 'Cancel',
  danger = false,
  optional = false,
  initialValue = '',
  onClose,
  onSubmit,
  testId,
}: ReasonDialogProps) {
  const [text, setText] = useState(initialValue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setText(initialValue);
    setSaving(false);
    setError(null);
  }, [isOpen, initialValue]);

  const trimmed = text.trim();
  const tooLong = trimmed.length > ONBOARDING_LIMITS.reason;
  const canSave = (optional || trimmed.length > 0) && !tooLong && !saving;

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await onSubmit(trimmed);
    } catch (err) {
      if (alive.current) setError(describeOnboardingError(err, 'That did not save. Try again.'));
    } finally {
      if (alive.current) setSaving(false);
    }
  };

  const fieldId = `lms-${testId}-text`;

  return (
    <Modal isOpen={isOpen} onClose={saving ? () => {} : onClose} title={title} maxWidth="max-w-lg">
      <form onSubmit={submit} className="space-y-4" data-testid={`${testId}-form`}>
        {intro ? <div className="text-sm text-gray-700 dark:text-gray-300">{intro}</div> : null}
        <div>
          <label htmlFor={fieldId} className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
            {label}
          </label>
          <textarea
            id={fieldId}
            className={`${fieldCls} w-full`}
            rows={3}
            value={text}
            maxLength={ONBOARDING_LIMITS.reason + 50}
            onChange={(e) => setText(e.target.value)}
            data-testid={`${testId}-text`}
          />
          {hint ? <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{hint}</p> : null}
          {tooLong ? (
            <p className="mt-1 text-xs text-red-700 dark:text-red-400">{`At most ${ONBOARDING_LIMITS.reason} characters.`}</p>
          ) : null}
        </div>

        {error ? (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
            data-testid={`${testId}-error`}
          >
            {error}
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={saving}>
            {cancelLabel}
          </button>
          <button type="submit" className={danger ? dangerBtnCls : primaryBtnCls} disabled={!canSave} data-testid={`${testId}-save`}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            {confirmLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default ReasonDialog;
